import React, { useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/apiService';
import './AudiosS3.css';

// Tela interna "Áudios no S3" (04/11/2026): procura o arquivo direto no bucket da empresa, sem depender de ele ter
// virado gravação no banco. Serve para achar chunk que não concatenou, recorte descartado, voz cadastrada etc.
//
// O bucket é o do AMBIENTE desta tela: em admin-qa lista os buckets de QA, em admin os de produção. A API monta os
// prefixos por loja e por dia (pasta AAAA/MM/DD) e lista em paralelo; os chunks voltam agrupados por sessão.
// O áudio passa pelo Express (link assinado de 60 s que não chega ao navegador) e fica só na memória da aba.

const TIPOS = [
  { id: 'gravacao', nome: 'Gravação', dica: 'Áudio concatenado na pasta da loja (no AWON, o recorte do atendimento)' },
  { id: 'redacao', nome: 'Anonimizada', dica: 'Cópia da gravação com os trechos sensíveis silenciados (redaction/)' },
  { id: 'chunk_app', nome: 'Chunks do app', dica: 'Pedaços de 20 s do modo manual, antes de concatenar (chunks/)' },
  { id: 'chunk_awon', nome: 'Chunks AWON', dica: 'Pedaços de 20 s da gravação contínua (awon/chunks/)' },
  { id: 'janela_awon', nome: 'Janela AWON', dica: 'Janela de comportamento do worker2 (awon/behavior_audio/)' },
  { id: 'descartado_awon', nome: 'Descartado AWON', dica: 'Recorte que o AWON descartou (awon/discarded/)' },
  { id: 'embedding', nome: 'Voz cadastrada', dica: 'Gravação de cadastro de voz do vendedor (embbeddings/). Não tem data na pasta: aparece em qualquer período' },
];
const NOME_TIPO = Object.fromEntries(TIPOS.map(t => [t.id, t.nome]));
const ORIGEM_FIXA = { chunk_app: 'manual', chunk_awon: 'awon', janela_awon: 'awon', descartado_awon: 'awon', embedding: 'awon' };
const MAX_DIAS = 31;
const POR_PAGINA = 100;

// Datas como TEXTO AAAA-MM-DD, nunca por `new Date('AAAA-MM-DD')` (que é meia-noite UTC e volta um dia no Brasil).
const hojeBRT = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
function somaDias(iso, n) {
  const [a, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(a, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}
function diasEntre(de, ate) {
  const p = s => { const [a, m, d] = s.split('-').map(Number); return Date.UTC(a, m - 1, d); };
  return Math.round((p(ate) - p(de)) / 86400000) + 1;
}
const fmtData = iso => (iso ? iso.split('-').reverse().join('/') : '—');
function fmtBytes(b) {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDur(s) {
  if (!s) return '';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return h ? `${h}h${String(m).padStart(2, '0')}` : `${m}:${String(ss).padStart(2, '0')}`;
}
function ambiente() {
  const label = (window.location.hostname.split('.')[0] || '').toLowerCase();
  if (label.endsWith('-qa')) return { id: 'qa', nome: 'QA' };
  if (label.endsWith('-hmg')) return { id: 'hmg', nome: 'Homologação' };
  if (label === 'localhost' || label === '127') return { id: 'dev', nome: 'Local' };
  return { id: 'prod', nome: 'Produção' };
}
function erroDe(e) {
  return e?.response?.data?.error || e?.message || 'falha ao carregar';
}

async function copiar(texto) {
  try { await navigator.clipboard.writeText(texto); return true; } catch (_) { return false; }
}

export default function AudiosS3() {
  const env = useMemo(ambiente, []);
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState('');
  const [opcoes, setOpcoes] = useState({ stores: [], salesmen: [], bucket: '' });
  const [loja, setLoja] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [de, setDe] = useState(hojeBRT());
  const [ate, setAte] = useState(hojeBRT());
  const [origem, setOrigem] = useState('all');
  const [tipos, setTipos] = useState(TIPOS.map(t => t.id));

  const [resultado, setResultado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState(null);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [abertos, setAbertos] = useState({});
  const [aviso, setAviso] = useState(null);

  const [tocando, setTocando] = useState(null); // { key, nome, src, carregando, erro }
  const [baixando, setBaixando] = useState({});
  const srcRef = useRef(null);

  useEffect(() => {
    apiService.s3AudiosCompanies()
      .then(d => {
        if (!Array.isArray(d?.companies)) throw new Error('resposta inesperada do servidor');
        setEmpresas(d.companies);
      })
      .catch(e => setErro(erroDe(e)));
  }, []);

  useEffect(() => {
    setLoja(''); setVendedor(''); setResultado(null);
    setOpcoes({ stores: [], salesmen: [], bucket: '' });
    if (!empresa) return;
    apiService.s3AudiosOptions(empresa)
      .then(d => {
        if (!Array.isArray(d?.stores)) throw new Error('resposta inesperada do servidor');
        setOpcoes(d);
      })
      .catch(e => setErro(erroDe(e)));
  }, [empresa]);

  useEffect(() => () => { if (srcRef.current) URL.revokeObjectURL(srcRef.current); }, []);

  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(null), 2500);
    return () => clearTimeout(t);
  }, [aviso]);

  const lojaSel = opcoes.stores.find(s => s.name === loja);
  const vendedoresDaLoja = useMemo(
    () => opcoes.salesmen.filter(v => !lojaSel || v.store_id === lojaSel.id),
    [opcoes.salesmen, lojaSel],
  );
  const lojaNome = useMemo(
    () => Object.fromEntries(opcoes.stores.map(s => [s.name, s.display_name || s.name])),
    [opcoes.stores],
  );

  const nDias = de && ate ? diasEntre(de, ate) : 0;
  const tiposValidos = tipos.filter(t => origem === 'all' || (ORIGEM_FIXA[t] || origem) === origem);
  const podeBuscar = empresa && de && ate && nDias >= 1 && nDias <= MAX_DIAS && tiposValidos.length > 0 && !buscando;

  const preset = n => { const h = hojeBRT(); setAte(n === 'ontem' ? somaDias(h, -1) : h); setDe(n === 'ontem' ? somaDias(h, -1) : somaDias(h, -(n - 1))); };

  const buscar = async () => {
    if (!podeBuscar) return;
    setBuscando(true); setErro(null); setPagina(0); setAbertos({}); setFiltroTipo('');
    try {
      const d = await apiService.s3AudiosSearch({
        subdomain: empresa, date_from: de, date_to: ate, origin: origem,
        types: tiposValidos.length === TIPOS.length ? undefined : tiposValidos.join(','),
        stores: loja || undefined,
        salesman_id: vendedor || undefined,
      });
      if (!Array.isArray(d?.items)) throw new Error('resposta inesperada do servidor');
      setResultado(d);
    } catch (e) {
      setErro(erroDe(e)); setResultado(null);
    } finally { setBuscando(false); }
  };

  const itensFiltrados = useMemo(() => {
    if (!resultado) return [];
    const q = filtroTexto.trim().toLowerCase();
    return resultado.items.filter(i => {
      if (filtroTipo && i.type !== filtroTipo) return false;
      if (!q) return true;
      return [i.key, i.session, i.username, i.salesman_name, i.device, i.store, lojaNome[i.store], String(i.salesman_id || '')]
        .some(v => v && String(v).toLowerCase().includes(q));
    });
  }, [resultado, filtroTexto, filtroTipo, lojaNome]);

  const contagem = useMemo(() => {
    const c = {};
    (resultado?.items || []).forEach(i => { c[i.type] = (c[i.type] || 0) + 1; });
    return c;
  }, [resultado]);

  const totalPaginas = Math.max(1, Math.ceil(itensFiltrados.length / POR_PAGINA));
  const visiveis = itensFiltrados.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA);

  const ouvir = async (key) => {
    if (srcRef.current) { URL.revokeObjectURL(srcRef.current); srcRef.current = null; }
    const nome = key.split('/').pop();
    setTocando({ key, nome, carregando: true });
    try {
      const blob = await apiService.s3AudiosArquivo(empresa, key);
      const url = URL.createObjectURL(blob);
      srcRef.current = url;
      setTocando(t => (t?.key === key ? { key, nome, src: url } : t));
    } catch (e) {
      setTocando(t => (t?.key === key ? { key, nome, erro: 'não foi possível carregar o áudio' } : t));
    }
  };

  const baixar = async (key) => {
    setBaixando(b => ({ ...b, [key]: true }));
    try {
      const blob = await apiService.s3AudiosArquivo(empresa, key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = key.split('/').pop();
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      setAviso('Falha ao baixar o arquivo');
    } finally { setBaixando(b => { const n = { ...b }; delete n[key]; return n; }); }
  };

  const copiarChave = async (texto, msg = 'Copiado') => setAviso((await copiar(texto)) ? msg : 'Não foi possível copiar');

  // Comando para baixar a sessão inteira de chunks pelo terminal (quem tem acesso à AWS).
  const comandoSessao = (g) => {
    const pasta = g.chunks[0].key.replace(/[^/]+$/, '');
    return `aws s3 cp "s3://${resultado.bucket}/${pasta}" . --recursive --exclude "*" --include "*__${g.session}__${g.salesman_id}__CHUNK-*"`;
  };

  const vendedorLabel = (i) => {
    const nome = i.salesman_name || i.username;
    if (!nome && !i.salesman_id) return '—';
    return (
      <>
        {nome || '—'}
        {i.salesman_id ? <span className="as3-muted"> #{i.salesman_id}</span> : null}
      </>
    );
  };

  const BotoesArquivo = ({ k }) => (
    <span className="as3-acoes">
      <button className="as3-btn-icone" title="Ouvir" onClick={() => ouvir(k)}>{tocando?.key === k ? '🔊' : '▶'}</button>
      <button className="as3-btn-icone" title="Baixar" disabled={!!baixando[k]} onClick={() => baixar(k)}>{baixando[k] ? '…' : '⬇'}</button>
      <button className="as3-btn-icone" title="Copiar caminho no S3" onClick={() => copiarChave(`s3://${resultado.bucket}/${k}`, 'Caminho copiado')}>⧉</button>
    </span>
  );

  return (
    <div className="as3-page">
      <div className="as3-cab">
        <h1 className="as3-title">🎧 Áudios no S3</h1>
        <span className={`as3-env as3-env-${env.id}`}>{env.nome}</span>
      </div>
      <p className="as3-help">
        Busca direto no bucket da empresa: gravações, chunks, cópias anonimizadas, recortes do AWON e vozes
        cadastradas, mesmo os que não viraram atendimento. O período segue a <strong>data da pasta</strong> no S3
        (dia da gravação). Esta tela enxerga os buckets de <strong>{env.nome}</strong>; para o outro ambiente, abra o
        admin dele.
      </p>

      <div className="as3-filtros">
        <label className="as3-campo">
          <span>Empresa</span>
          <select value={empresa} onChange={e => setEmpresa(e.target.value)}>
            <option value="">Selecione…</option>
            {empresas.map(c => <option key={c.subdomain} value={c.subdomain}>{c.name} ({c.subdomain})</option>)}
          </select>
        </label>
        <label className="as3-campo">
          <span>Loja</span>
          <select value={loja} onChange={e => { setLoja(e.target.value); setVendedor(''); }} disabled={!empresa}>
            <option value="">Todas</option>
            {opcoes.stores.map(s => (
              <option key={s.id} value={s.name}>{s.display_name || s.name}{s.deleted ? ' (apagada)' : ''}</option>
            ))}
          </select>
        </label>
        <label className="as3-campo">
          <span>Vendedor</span>
          <select value={vendedor} onChange={e => setVendedor(e.target.value)} disabled={!empresa}>
            <option value="">Todos</option>
            {vendedoresDaLoja.map(v => (
              <option key={v.id} value={v.id}>{v.fullname || v.username} · {v.username} #{v.id}{v.deleted ? ' (inativo)' : ''}</option>
            ))}
          </select>
        </label>
        <div className="as3-campo">
          <span>Período</span>
          <div className="as3-periodo">
            <input type="date" value={de} max={ate || undefined} onChange={e => setDe(e.target.value)} />
            <span className="as3-muted">até</span>
            <input type="date" value={ate} min={de || undefined} onChange={e => setAte(e.target.value)} />
          </div>
          <div className="as3-presets">
            <button onClick={() => preset(1)}>Hoje</button>
            <button onClick={() => preset('ontem')}>Ontem</button>
            <button onClick={() => preset(7)}>7 dias</button>
            <button onClick={() => preset(30)}>30 dias</button>
          </div>
        </div>
        <div className="as3-campo">
          <span>Origem</span>
          <div className="as3-seg">
            {[['all', 'Todas'], ['manual', 'Manual'], ['awon', 'AWON']].map(([id, nome]) => (
              <button key={id} className={origem === id ? 'on' : ''} onClick={() => setOrigem(id)}>{nome}</button>
            ))}
          </div>
        </div>
        <div className="as3-campo as3-campo-largo">
          <span>
            Tipos
            <button className="as3-link" onClick={() => setTipos(TIPOS.map(t => t.id))}>todos</button>
            <button className="as3-link" onClick={() => setTipos([])}>nenhum</button>
          </span>
          <div className="as3-chips">
            {TIPOS.map(t => {
              const fora = origem !== 'all' && ORIGEM_FIXA[t.id] && ORIGEM_FIXA[t.id] !== origem;
              const on = tipos.includes(t.id);
              return (
                <label key={t.id} className={`as3-chip ${on && !fora ? 'on' : ''} ${fora ? 'fora' : ''}`} title={fora ? `Só existe na origem ${ORIGEM_FIXA[t.id] === 'awon' ? 'AWON' : 'manual'}` : t.dica}>
                  <input type="checkbox" checked={on} disabled={fora}
                    onChange={() => setTipos(ts => (ts.includes(t.id) ? ts.filter(x => x !== t.id) : [...ts, t.id]))} />
                  {t.nome}
                </label>
              );
            })}
          </div>
        </div>
        <div className="as3-campo as3-campo-buscar">
          <button className="as3-buscar" disabled={!podeBuscar} onClick={buscar}>{buscando ? 'Buscando…' : 'Buscar'}</button>
          {nDias > MAX_DIAS && <span className="as3-erro-inline">Máximo de {MAX_DIAS} dias</span>}
          {nDias < 1 && de && ate && <span className="as3-erro-inline">Data final antes da inicial</span>}
        </div>
      </div>

      {erro && <div className="as3-erro">{erro}</div>}
      {buscando && <div className="as3-carregando">Listando o bucket… períodos longos de redes grandes podem levar até 30 s.</div>}

      {resultado && !buscando && (
        <>
          <div className="as3-resumo">
            <span><strong>{resultado.total.toLocaleString('pt-BR')}</strong> itens em <code>{resultado.bucket}</code></span>
            <span className="as3-muted">{resultado.prefixes_listed} pastas lidas em {(resultado.elapsed_ms / 1000).toFixed(1)} s</span>
            {resultado.errors > 0 && <span className="as3-erro-inline">{resultado.errors} pasta(s) falharam na leitura</span>}
          </div>
          {resultado.truncated && (
            <div className="as3-alerta">
              Mostrando os {resultado.max_items.toLocaleString('pt-BR')} mais recentes de {resultado.total.toLocaleString('pt-BR')}.
              Reduza o período ou escolha loja, vendedor ou tipo para ver o resto.
            </div>
          )}
          <div className="as3-barra">
            <div className="as3-chips">
              <button className={`as3-chip ${!filtroTipo ? 'on' : ''}`} onClick={() => { setFiltroTipo(''); setPagina(0); }}>
                Todos ({resultado.items.length})
              </button>
              {TIPOS.filter(t => contagem[t.id]).map(t => (
                <button key={t.id} className={`as3-chip ${filtroTipo === t.id ? 'on' : ''}`} onClick={() => { setFiltroTipo(t.id); setPagina(0); }}>
                  {t.nome} ({contagem[t.id]})
                </button>
              ))}
            </div>
            <input className="as3-busca" placeholder="Filtrar resultado (nome, arquivo, aparelho, #id)…"
              value={filtroTexto} onChange={e => { setFiltroTexto(e.target.value); setPagina(0); }} />
          </div>

          {itensFiltrados.length === 0 ? (
            <div className="as3-vazio">Nenhum áudio encontrado com esses filtros.</div>
          ) : (
            <div className="as3-tabela-wrap">
              <table className="as3-tabela">
                <thead>
                  <tr>
                    <th>Data / hora</th>
                    <th>Loja</th>
                    <th>Vendedor</th>
                    <th>Tipo</th>
                    <th>Origem</th>
                    <th>Arquivo</th>
                    <th className="as3-num">Tamanho</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visiveis.map(i => {
                    const id = i.key || `${i.type}|${i.store}|${i.session}|${i.salesman_id}`;
                    if (i.kind === 'chunks') {
                      const aberto = !!abertos[id];
                      return (
                        <React.Fragment key={id}>
                          <tr className="as3-linha-grupo">
                            <td>{fmtData(i.date)} <span className="as3-muted">{i.time}</span></td>
                            <td>{lojaNome[i.store] || i.store}</td>
                            <td>{vendedorLabel(i)}</td>
                            <td><span className={`as3-tag as3-tag-${i.type}`}>{NOME_TIPO[i.type]}</span></td>
                            <td><span className={`as3-origem as3-origem-${i.origin}`}>{i.origin === 'awon' ? 'AWON' : 'Manual'}</span></td>
                            <td>
                              <button className="as3-link" onClick={() => setAbertos(a => ({ ...a, [id]: !aberto }))}>
                                {aberto ? '▾' : '▸'} sessão {i.session} · {i.count} chunks
                                {i.est_duration_s ? ` · ~${fmtDur(i.est_duration_s)}` : ''}
                              </button>
                              <div className="as3-muted as3-pequeno">
                                {i.device}{i.behaviour ? ' · behaviour' : ''} · CHUNK-{i.first_n} a {i.last_n}
                                {i.has_last ? ' · com -LAST' : ' · sem -LAST (sessão aberta ou interrompida)'}
                                {i.missing > 0 && <span className="as3-erro-inline"> · {i.missing} faltando na numeração</span>}
                              </div>
                            </td>
                            <td className="as3-num">{fmtBytes(i.size)}</td>
                            <td>
                              <span className="as3-acoes">
                                <button className="as3-btn-icone" title="Copiar comando aws s3 cp da sessão" onClick={() => copiarChave(comandoSessao(i), 'Comando copiado')}>⌘</button>
                                <button className="as3-btn-icone" title="Copiar a lista de caminhos" onClick={() => copiarChave(i.chunks.map(c => `s3://${resultado.bucket}/${c.key}`).join('\n'), 'Caminhos copiados')}>⧉</button>
                              </span>
                            </td>
                          </tr>
                          {aberto && i.chunks.map(c => (
                            <tr key={c.key} className="as3-linha-chunk">
                              <td />
                              <td colSpan={4} className="as3-muted as3-pequeno">{c.last_modified ? new Date(c.last_modified).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : ''} enviado</td>
                              <td className="as3-arquivo">CHUNK-{c.n}{c.last ? ' (LAST)' : ''}</td>
                              <td className="as3-num">{fmtBytes(c.size)}</td>
                              <td><BotoesArquivo k={c.key} /></td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    }
                    return (
                      <tr key={id} className={tocando?.key === i.key ? 'as3-tocando' : ''}>
                        <td>{i.type === 'embedding' && !i.time ? <span className="as3-muted">{fmtData(i.date)}</span> : <>{fmtData(i.date)} <span className="as3-muted">{i.time || ''}</span></>}</td>
                        <td>{lojaNome[i.store] || i.store}</td>
                        <td>{vendedorLabel(i)}</td>
                        <td><span className={`as3-tag as3-tag-${i.type}`}>{NOME_TIPO[i.type]}</span></td>
                        <td><span className={`as3-origem as3-origem-${i.origin}`}>{i.origin === 'awon' ? 'AWON' : 'Manual'}</span></td>
                        <td className="as3-arquivo" title={i.key}>{i.key.split('/').pop()}</td>
                        <td className="as3-num">{fmtBytes(i.size)}</td>
                        <td><BotoesArquivo k={i.key} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalPaginas > 1 && (
            <div className="as3-paginacao">
              <button disabled={pagina === 0} onClick={() => setPagina(p => p - 1)}>‹ Anterior</button>
              <span>Página {pagina + 1} de {totalPaginas} · {itensFiltrados.length.toLocaleString('pt-BR')} itens</span>
              <button disabled={pagina >= totalPaginas - 1} onClick={() => setPagina(p => p + 1)}>Próxima ›</button>
            </div>
          )}
        </>
      )}

      {tocando && (
        <div className="as3-player">
          <div className="as3-player-nome" title={tocando.key}>{tocando.nome}</div>
          {tocando.carregando && <span className="as3-muted">carregando…</span>}
          {tocando.erro && <span className="as3-erro-inline">{tocando.erro}</span>}
          {tocando.src && <audio src={tocando.src} controls autoPlay />}
          <button className="as3-btn-icone" title="Fechar" onClick={() => {
            if (srcRef.current) { URL.revokeObjectURL(srcRef.current); srcRef.current = null; }
            setTocando(null);
          }}>✕</button>
        </div>
      )}

      {aviso && <div className="as3-toast">{aviso}</div>}
    </div>
  );
}
